import type { ArenaConfig } from "../config/gameplay-config";
import type { Vector2 } from "../simulation/player-movement";
import { interpolateHeading } from "./render-interpolation";

export interface CameraSubject {
  readonly directionRadians: number;
  readonly position: Vector2;
}

export interface CameraViewport {
  readonly height: number;
  readonly width: number;
}

const clampCameraCoordinate = (
  desired: number,
  worldSize: number,
  viewportSize: number,
): number => {
  if (viewportSize >= worldSize) {
    return worldSize / 2;
  }
  const halfViewport = viewportSize / 2;
  return Math.min(worldSize - halfViewport, Math.max(halfViewport, desired));
};

export const calculateArenaCameraTarget = (
  subject: CameraSubject,
  arena: ArenaConfig,
  viewport: CameraViewport,
): Vector2 => {
  const desiredX =
    subject.position.x +
    Math.cos(subject.directionRadians) * arena.cameraLookAheadDistance;
  const desiredY =
    subject.position.y +
    Math.sin(subject.directionRadians) * arena.cameraLookAheadDistance;
  return {
    x: clampCameraCoordinate(desiredX, arena.width, viewport.width),
    y: clampCameraCoordinate(desiredY, arena.height, viewport.height),
  };
};

export const smoothArenaCameraTarget = (
  current: Vector2,
  desired: Vector2,
  followRatePerSecond: number,
  elapsedMilliseconds: number,
): Vector2 => {
  const progress =
    1 -
    Math.exp(
      -Math.max(0, followRatePerSecond) *
        (Math.max(0, elapsedMilliseconds) / 1_000),
    );
  return {
    x: current.x + (desired.x - current.x) * progress,
    y: current.y + (desired.y - current.y) * progress,
  };
};

export const smoothArenaCameraHeading = (
  current: number,
  desired: number,
  followRatePerSecond: number,
  elapsedMilliseconds: number,
): number => {
  const progress =
    1 -
    Math.exp(
      -Math.max(0, followRatePerSecond) *
        (Math.max(0, elapsedMilliseconds) / 1_000),
    );
  return interpolateHeading(current, desired, progress);
};
